import React, { useEffect, useState } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";

import Accordion from "veneer/Accordion";
import AccordionDetails from "veneer/AccordionDetails";
import AccordionSummary from "veneer/AccordionSummary";
import Alert from "veneer/Alert";
import Button from "veneer/Button";
import Divider from "veneer/Divider";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import TextInput from "veneer/TextInput";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { useAppSelector, useFeatureFlag } from "cortex/ui/src/hooks";
import {
  authSelector,
  isAuthenticated,
  isSessionExpired,
} from "cortex/ui/src/redux-store/auth/selectors";
import { storesReady } from "cortex/ui/src/redux-store/utils";
import { BasicLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import Auth0CallbackHandler from "cortex/ui/src/view/auth/Auth0CallbackHandler";

import BaseLoginView from "./BaseLoginView";

interface LocationState {
  nextLocation?: {
    pathname?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

type FormValues = {
  email: string;
  password: string;
};

const SsoLoginButton = styled(Button)({
  backgroundColor: Colors.gray1000,
  "&:hover": {
    backgroundColor: Colors.gray900,
  },
});

const CredentialAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  width: "100%",
}));

const CredentialAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

const CredentialForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export default function Login() {
  const location = useLocation<LocationState>();
  const auth = useAppSelector((state) => authSelector(state));
  const sessionExpired = isSessionExpired(auth);
  const isAuthenticatedUser = isAuthenticated(auth);
  const storesLoaded = useAppSelector((state) =>
    storesReady((state as any).global.auth),
  );
  const isSsoLoginEnabled = useFeatureFlag("sso-login");
  const isLoginCollapsedEnabled = useFeatureFlag("is-login-collapsed-enabled");
  const shouldCollapseLogin = isSsoLoginEnabled && isLoginCollapsedEnabled;

  const [form, setForm] = useState<FormValues>({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isPasswordLoginExpanded, setIsPasswordLoginExpanded] =
    useState<boolean>(false);

  useEffect(() => {
    if (sessionExpired) {
      setErrorMessage("Your session has expired. Please log in again.");
    }
  }, [sessionExpired]);

  const state = location.state || ({} as LocationState);
  const redirectUrl =
    state.nextLocation?.pathname && state.nextLocation.pathname !== "/login"
      ? state.nextLocation.pathname
      : "/";

  if (!storesLoaded) {
    return <BasicLoader />;
  }

  if (isAuthenticatedUser) {
    return <Redirect to={redirectUrl} />;
  }

  const tokenFromParams =
    new URLSearchParams(location.search).get("token") || "";

  const credentialForm = (
    <CredentialForm
      action="/api/auth"
      data-testid="login-credentials-form"
      method="POST"
    >
      <input name="redirect" type="hidden" value={redirectUrl} />
      <input name="token" type="hidden" value={tokenFromParams} />
      <input name="email" type="hidden" value={form.email} />
      <input name="password" type="hidden" value={form.password} />
      <TextInput
        autoComplete="username"
        fullWidth
        label="Email"
        onChange={(value) => setForm({ ...form, email: value ?? "" })}
        size="medium"
        value={form.email}
      />
      <TextInput
        autoComplete="current-password"
        fullWidth
        label="Password"
        onChange={(value) => setForm({ ...form, password: value ?? "" })}
        size="medium"
        type="password"
        value={form.password}
      />
      <Link to="/login/forgot-password">
        <Button variant="text">Forgot Password?</Button>
      </Link>

      <Button fullWidth size="large" submit>
        Log In
      </Button>
    </CredentialForm>
  );

  return (
    <BaseLoginView>
      <Auth0CallbackHandler />
      <Typography align="center">Log in to start your session</Typography>
      <Notification error={errorMessage} />

      {tokenFromParams && (
        <Alert icon={null} severity="warning" title="Assuming session">
          On logging in you will assume the user of an alternate account.
        </Alert>
      )}

      {isSsoLoginEnabled && (
        <>
          <a
            href={`/api/auth/sso${tokenFromParams ? `?token=${tokenFromParams}` : ""}`}
          >
            <SsoLoginButton fullWidth icon="key" size="large">
              Log In With Vistar
            </SsoLoginButton>
          </a>
          <Stack alignItems="center" direction="row" gap={1} width="100%">
            <Divider flexItem />
            <Typography variant="body2">OR</Typography>
            <Divider flexItem />
          </Stack>
        </>
      )}

      {shouldCollapseLogin ? (
        <CredentialAccordion
          dataTestId="login-credentials-accordion"
          expanded={isPasswordLoginExpanded}
          onChange={(_event, isExpanded) =>
            setIsPasswordLoginExpanded(isExpanded)
          }
        >
          <AccordionSummary dataTestId="login-credentials-accordion-summary">
            <Typography>Use Fleet email and password</Typography>
          </AccordionSummary>
          <CredentialAccordionDetails dataTestId="login-credentials-accordion-details">
            {credentialForm}
          </CredentialAccordionDetails>
        </CredentialAccordion>
      ) : (
        credentialForm
      )}
    </BaseLoginView>
  );
}
